import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    ArrowLeft,
    ArrowRight,
    CalendarDays,
    FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { APP_URL, V1_API_ORIGIN } from "../../../config/constants.config";
import { useDocumentTitle } from "../../../utils/Common";
import Layout from "../../layout/Layout";
import "./ViewReports.css";

const getReportDate = (report) =>
    report?.reportDate ||
    report?.publishedAt ||
    report?.createdAt ||
    report?.date ||
    report?.updatedAt;

const getReportName = (report) => {
    return report?.name || "Multibagger report";
}    

const getReportUrl = (report) => {
    return report?.documentPath
    // report?.multibaggerReportPath ||
}

const getReportsArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.reports)) return data.reports;
    if (Array.isArray(data?.dataList)) return data.dataList;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
};

const monthFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "numeric",
});

export default function ViewReports() {
    const navigate = useNavigate();
    const [reportData, setReportData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const uid = localStorage.getItem("uid");

    useDocumentTitle("Multibagger Reports");

    useEffect(() => {
        const controller = new AbortController();

        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError("");
                const response = await axios.get(
                    `${V1_API_ORIGIN}/reports/get-reports`,
                    {
                        params: { userId: uid, type: "multibagger" },
                        signal: controller.signal,
                    },
                );
                setReportData(getReportsArray(response?.data?.data));
            } catch (requestError) {
                if (requestError.code !== "ERR_CANCELED") {
                    setError("We couldn't load your reports. Please try again.");
                }
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };

        fetchData();
        return () => controller.abort();
    }, [uid]);

    const groupedReports = useMemo(() => {
        const years = new Map();

        reportData
            .map((report, index) => {
                const value = getReportDate(report);
                const date = value ? new Date(value) : null;
                return {
                    report,
                    index,
                    date: date && !Number.isNaN(date.getTime()) ? date : null,
                };
            })
            .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0))
            .forEach((item) => {
                const yearKey = item.date ? String(item.date.getFullYear()) : "Other";
                const monthKey = item.date
                    ? `${yearKey}-${item.date.getMonth()}`
                    : "undated";

                if (!years.has(yearKey)) {
                    years.set(yearKey, { label: yearKey, months: new Map() });
                }

                const year = years.get(yearKey);
                if (!year.months.has(monthKey)) {
                    year.months.set(monthKey, {
                        label: item.date ? monthFormatter.format(item.date) : "Reports",
                        reports: [],
                    });
                }
                year.months.get(monthKey).reports.push(item);
                
                
            });

        return Array.from(years.values())
            .slice(0, 6)
            .map((year) => ({
                ...year,
                months: Array.from(year.months.values()),
            }));
    }, [reportData]);

    const openReport = (report) => {
        const url = getReportUrl(report);
        if (url) window.open(process.env.REACT_APP_REMOTE_ORIGIN+`/api/storage/`+url, "_blank", "noopener,noreferrer");
    };

    return (
        <Layout>
            <section className=" py-3">
                <div className="container">
                    <div class="col-md-12 mb-2">
                        <a
                            href="/multibagger"
                            class="text-decoration-none text-body-emphasis fs-5"
                            aria-label="Back to dashboard"
                        >
                            <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        </a>
                    </div>
                    <div className="col-md-8 mb-2">
                        <div className="row g-3 align-items-center summary-stats-row">
                            <div class="col-md-12">
                                <div class="d-flex justify-content-start align-items-start gap-3">
                                    <div>
                                        <h4 class="mb-1 fw-bold">Multibagger Reports</h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {isLoading && (
                        <div className="reports-status" role="status">
                            <span className="reports-spinner" />
                            Loading reports...
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="reports-status reports-error" role="alert">
                            {error}
                        </div>
                    )}

                    {!isLoading && !error && groupedReports.length === 0 && (
                        <div className="reports-status">
                            <FileText size={38} aria-hidden="true" />
                            <span>No multibagger reports are available yet.</span>
                        </div>
                    )}

                    {!isLoading && !error && (
                        <div className="reports-groups">
                            {groupedReports.map((year) => (
                                <section className="reports-year" key={year.label}>
                                    <div className="reports-year-heading">
                                        <h2>{year.label}</h2>
                                        <span>
                                            {year.months.reduce(
                                                (total, month) => total + month.reports.length,
                                                0,
                                            )} reports
                                        </span>
                                    </div>

                                    <div className="reports-year-content">
                                        {year.months.map((month) => (
                                            <section className="reports-month" key={month.label}>
                                                <h3>
                                                    <CalendarDays aria-hidden="true" />
                                                    {month.label}
                                                </h3>

                                                <div className="reports-list">
                                                    {month.reports[0]?.report?.reports?.map(( report, index ) => {
                                                        const reportUrl = getReportUrl(report);

                                                        
                                                        return (
                                                            <article
                                                                className="report-card"
                                                                key={
                                                                    report?.id ||
                                                                    report?._id ||
                                                                    `${month.label}-${index}`
                                                                }
                                                            >
                                                                <span className="report-file-icon" aria-hidden="true">
                                                                    <FileText />
                                                                    <small>PDF</small>
                                                                </span>
                                                                <h4 title={getReportName(report)}>
                                                                    {getReportName(report)}
                                                                </h4>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openReport(report)}
                                                                    disabled={!reportUrl}
                                                                    aria-label={`View ${getReportName(report)}`}
                                                                >
                                                                    <span>View</span>
                                                                    <ArrowRight aria-hidden="true" />
                                                                </button>
                                                            </article>
                                                        );
                                                    })}
                                                </div>
                                            </section>
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </Layout>
    );
}
